import { PersistStorage } from "easy-peasy";
import { TalentProfile, User } from "src/graphql/types";
import { isKnownVertical } from "src/utils/user";

/*
 * A saved draft can name a vertical this build does not know: one written by
 * a newer build before a rollback, or taken from the API by a tab that
 * predates the vertical. The persisted models drop the whole entry that names
 * it, never just that part: a shortlist read back one person short is sent as
 * the job's shortlist, and silently takes them off it.
 */

// A talent with no vertical names nothing: nothing here can tell what they are.
export const namesUnknownTalent = (users: User[] | null | undefined) =>
  (users || []).some((user) => {
    const vertical = (user?.profile as TalentProfile | undefined)?.vertical;
    return !!vertical && !isKnownVertical(vertical);
  });

export const namesUnknownVertical = (
  items: { vertical: string }[] | null | undefined,
) => (items || []).some(({ vertical }) => !isKnownVertical(vertical));

/*
 * easy-peasy's transformers read a saved model back one key at a time, so
 * they cannot drop a draft whose keys refer to each other. This reads it back
 * whole, or not at all when `usable` refuses it, leaving the model's defaults.
 * It writes easy-peasy's own localStorage format, so earlier saves still load.
 */
export const wholeDraftStorage = <T>(
  usable: (saved: T) => boolean,
): PersistStorage => {
  const storage = () => {
    try {
      return window.localStorage;
    } catch {
      return undefined;
    }
  };

  return {
    getItem: (key) => {
      const saved = storage()?.getItem(key);
      if (saved == null) {
        return undefined;
      }
      const { data } = JSON.parse(saved);
      return data != null && usable(data) ? data : undefined;
    },
    setItem: (key, data) => storage()?.setItem(key, JSON.stringify({ data })),
    removeItem: (key) => storage()?.removeItem(key),
  };
};
